/**
 * Utility functions for the schedule panel.
 */

import type { ScheduleStop } from '../types/api';

/** Find the index of the next upcoming stop. */
export function findNextStopIndex(stops: ScheduleStop[]): number {
  // Primary: first non-cancelled stop with real-time prediction
  for (let i = 0; i < stops.length; i++) {
    if (stops[i].is_cancelled) continue;
    if (stops[i].has_realtime) return i;
  }
  // Fallback: first non-cancelled stop with planned departure in the future
  const now = Date.now();
  for (let i = 0; i < stops.length; i++) {
    if (stops[i].is_cancelled) continue;
    if (stops[i].planned_departure) {
      const planned = new Date(stops[i].planned_departure!).getTime();
      if (planned > now - 120_000) return i; // 2min grace for dwell time
    }
  }
  return stops.length; // Journey complete
}

/** Compute delay between estimated and planned departure. */
export function computeDelay(
  stop: ScheduleStop
): { text: string; colorClass: string } | null {
  if (!stop.estimated_departure || !stop.planned_departure) return null;
  const diffMs =
    new Date(stop.estimated_departure).getTime() -
    new Date(stop.planned_departure).getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (Math.abs(diffMin) < 1) {
    return {
      text: 'On time',
      colorClass: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
    };
  }
  if (diffMin > 0) {
    return {
      text: `+${diffMin} min`,
      colorClass: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
    };
  }
  return {
    text: `${diffMin} min`,
    colorClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
  };
}

/** Format an ISO 8601 timestamp as HH:mm (24h, Swedish convention). */
export function fmtHHMM(iso: string | null | undefined): string {
  if (!iso) return '--:--';
  const d = new Date(iso);
  return d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
}
